// SeeTex Trigger Engine
// Maps snippet triggers to MathLive insert commands
// Handles: inline shortcuts, structural commands, regex triggers, choice cycling

import type { TriggerDefinition, TriggerMatch, ChoiceState } from "./types";

export class TriggerEngine {
    private triggers: TriggerDefinition[] = [];
    private buffer: string = "";
    private maxBufferLen: number = 20;
    private choiceStates: Map<string, ChoiceState> = new Map();
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private debounceMs: number = 100;

    constructor() { }

    registerTriggers(triggers: TriggerDefinition[]) {
        // Expand aliases: for each trigger with aliases, create cloned entries
        // with the alias as the trigger string so existing matching logic works.
        const expanded: TriggerDefinition[] = [];
        for (const t of triggers) {
            expanded.push(t);
            if (t.aliases && typeof t.trigger === "string") {
                for (const alias of t.aliases) {
                    expanded.push({ ...t, trigger: alias, id: `${t.id}__alias_${alias}` });
                }
            }
        }

        // Sort by trigger length descending for longest-match-first
        this.triggers = [
            ...this.triggers,
            ...expanded,
        ].sort((a, b) => {
            const aLen = typeof a.trigger === "string" ? a.trigger.length : 0;
            const bLen = typeof b.trigger === "string" ? b.trigger.length : 0;
            return bLen - aLen;
        });
    }

    /**
     * Feed a character into the buffer. Returns a TriggerMatch if a trigger fires.
     */
    feedChar(char: string): TriggerMatch | null {
        this.buffer += char;
        if (this.buffer.length > this.maxBufferLen) {
            this.buffer = this.buffer.slice(-this.maxBufferLen);
        }

        // Try to match triggers (longest first)
        for (const trigger of this.triggers) {
            const match = this.tryMatch(trigger);
            if (match) {
                // Remove matched chars from buffer
                this.buffer = this.buffer.slice(0, -match.consumed);
                return match;
            }
        }

        return null;
    }

    private tryMatch(def: TriggerDefinition): TriggerMatch | null {
        if (def.isRegex && def.regexPattern) {
            const regex = new RegExp(def.regexPattern + "$");
            const m = this.buffer.match(regex);
            if (m) {
                return {
                    trigger: def,
                    consumed: m[0].length,
                    captures: m.slice(1),
                };
            }
        } else if (typeof def.trigger === "string") {
            if (this.buffer.endsWith(def.trigger)) {
                return {
                    trigger: def,
                    consumed: def.trigger.length,
                    captures: [],
                };
            }
        }
        return null;
    }

    /**
     * Cycle to the next choice for a given trigger ID.
     * Returns the new latex string or null if no choice available.
     */
    cycleChoice(triggerId: string, direction: 1 | -1): string | null {
        const state = this.choiceStates.get(triggerId);
        if (!state) return null;

        state.currentIndex =
            (state.currentIndex + direction + state.choices.length) %
            state.choices.length;
        this.choiceStates.set(triggerId, state);
        return state.choices[state.currentIndex];
    }

    /**
     * Register an active choice for tracking
     */
    setActiveChoice(triggerId: string, choices: string[]) {
        this.choiceStates.set(triggerId, {
            choices,
            currentIndex: 0,
        });
    }

    getActiveChoice(triggerId: string): ChoiceState | undefined {
        return this.choiceStates.get(triggerId);
    }

    clearActiveChoice(triggerId: string) {
        this.choiceStates.delete(triggerId);
    }

    clearBuffer() {
        this.buffer = "";
    }

    getBuffer(): string {
        return this.buffer;
    }

    /**
     * Append a character to the buffer without checking for trigger matches.
     * Use when auto-expand is off — keeps the buffer in sync for later checkBuffer().
     */
    appendChar(char: string) {
        this.buffer += char;
        if (this.buffer.length > this.maxBufferLen) {
            this.buffer = this.buffer.slice(-this.maxBufferLen);
        }
    }

    /**
     * Check the current buffer for a trigger match without feeding a new char.
     * If found, consumes the matched chars from the buffer and returns the match.
     */
    checkBuffer(): TriggerMatch | null {
        for (const trigger of this.triggers) {
            const match = this.tryMatch(trigger);
            if (match) {
                this.buffer = this.buffer.slice(0, -match.consumed);
                return match;
            }
        }
        return null;
    }

    clearDebounce() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }
}
